import {
  DEFAULT_PRODUCT_KIND,
  type OrganizationProduct,
  type ProductKind,
  type UpdateOrganizationProductInput,
} from '../types';
import { dedupeAliases, normalizeProductName } from './productNaming';

export interface ProductTextFields {
  name: string;
  description: string;
  priceRange: string;
}

export interface ProductFormFields extends ProductTextFields {
  kind: ProductKind;
  aliases: string[];
}

export const EMPTY_PRODUCT_FIELDS: ProductFormFields = {
  name: '',
  description: '',
  priceRange: '',
  kind: DEFAULT_PRODUCT_KIND,
  aliases: [],
};

export const productToFormFields = (product: OrganizationProduct): ProductFormFields => ({
  name: product.name,
  description: product.description ?? '',
  priceRange: product.priceRange ?? '',
  kind: product.kind ?? DEFAULT_PRODUCT_KIND,
  aliases: dedupeAliases(product.aliases ?? []),
});

export const buildCreateProductExtras = (fields: ProductFormFields) => {
  const description = fields.description.trim();
  const priceRange = fields.priceRange.trim();
  return {
    ...(description ? { description } : {}),
    ...(priceRange ? { priceRange } : {}),
    ...(fields.kind === DEFAULT_PRODUCT_KIND ? {} : { kind: fields.kind }),
    ...(fields.aliases.length > 0 ? { aliases: fields.aliases } : {}),
  };
};

const aliasKeys = (aliases: readonly string[]): string[] => aliases.map(normalizeProductName);

const sameAliases = (left: readonly string[], right: readonly string[]): boolean => {
  const leftKeys = aliasKeys(left);
  const rightKeys = aliasKeys(right);
  return leftKeys.length === rightKeys.length && leftKeys.every((key, i) => key === rightKeys[i]);
};

export const buildProductUpdateChanges = (
  loaded: OrganizationProduct,
  fields: ProductFormFields,
): Pick<UpdateOrganizationProductInput, 'kind' | 'aliases'> => {
  const loadedFields = productToFormFields(loaded);
  return {
    ...(fields.kind === loadedFields.kind ? {} : { kind: fields.kind }),
    ...(sameAliases(fields.aliases, loadedFields.aliases) ? {} : { aliases: fields.aliases }),
  };
};
